import { t } from '@lingui/core/macro';
import * as React from 'react';
import { endSession, syncSnapshot, userSession } from 'state/session';
import { myCameraStream } from 'state/video';
import { voipRtcConnection } from 'utils/ui/rtc';
import { IShowroomProps } from 'components/Showroom';

const useUnloadEvents = (userType: IShowroomProps['userType']) => {
  const isReloading = React.useRef(false);

  React.useEffect(() => {
    if (userType === 'dealer') {
      registerUnloadEvents();
    }
    return () => {
      cleanupUnloadEvents();
    };
    // eslint-disable-next-line
  }, []);

  function registerUnloadEvents() {
    document.onkeydown = handleKeyDown;
    window.onload = handleLoad;
    window.onbeforeunload = handleBeforeUnload;
    window.onpagehide = handleUnload;
  }

  function cleanupUnloadEvents() {
    document.onkeydown = null;
    window.onload = null;
    window.onbeforeunload = null;
    window.onpagehide = null;
  }

  function handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'F5' || (event.ctrlKey && event.key === 'r') || (event.metaKey && event.key === 'r')) {
      isReloading.current = true;
    }
  }

  function handleLoad() {
    isReloading.current = false;
  }

  function handleBeforeUnload(event: BeforeUnloadEvent) {
    const session = userSession().get();
    // its safe to reload if session.id is not set or session is not shared
    if (isReloading.current || !session.id || !session.isShared) {
      return;
    }
    const confirmationMessage = t`Are you sure you want to leave this page?`;
    event.preventDefault();
    Object.defineProperty(event, 'returnValue', {
      value: confirmationMessage,
    });
    return confirmationMessage;
  }

  function handleUnload() {
    if (isReloading.current) {
      syncSnapshot();
      return;
    }
    if (import.meta.env.MODE !== 'development') {
      // ensure proper cleanup and media allocation release
      myCameraStream()?.destroy();
      voipRtcConnection?.destroy();
      // don't end session when developing because it's uber frustrating
      endSession({ keepAlive: true });
    }
  }
};

export default useUnloadEvents;
